import { useEffect, useMemo, useState } from 'react';
import { BellPlus, BellRing, History, MoreHorizontal, Pause, Pencil, Play, Plus, RotateCcw, SlidersHorizontal, Trash2, X } from 'lucide-react';
import {
  EVENT_LABEL,
  offsetText,
  recipientsText,
  REMINDER_EVENTS,
  REMINDER_STATUS_LABEL,
  REMINDER_TEMPLATES,
  SCOPE_LABEL,
  type HolidayRegion,
  type ReminderEvent,
  type ReminderOffset,
  type ReminderPreview,
  type ReminderRule,
  type ReminderScope,
  type ReminderStage,
  type ReminderTrigger,
} from '@kaba/shared';
import { api } from '../../lib/api';
import { accountPref } from '../../lib/boardNav';
import { HolidaySelect } from '../HolidaySelect';
import {
  formatWhen,
  refreshReminders,
  snooze,
  snoozeChoices,
  useBoardReminders,
  useCardReminders,
  useReminderRuns,
  useReminderTargets,
  type ReminderTargets,
} from '../../lib/reminders';
import { toast } from '../../lib/toast';
import { Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownTrigger, IconButton, Sheet } from '../ui/ui';
import { Section, SectionAction } from './Section';

const field = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base sm:min-h-10 sm:text-sm';

type Column = { id: string; title: string };

interface Ctx {
  boardId: string;
  cardId: string | null;
  columns: Column[];
  /** Bearbeiter: Kartenregeln; Besitzer: Boardstandards */
  canEditCard: boolean;
  isOwner: boolean;
  meId: string | undefined;
}

/** Ein Satz je Stufe: „24 Stunden vor Fälligkeit · An alle Zuständigen“ */
function stageLine(rule: Pick<ReminderRule, 'trigger' | 'scope'>, s: ReminderStage, ctx: Ctx, targets: ReminderTargets | undefined) {
  const col = rule.trigger.type === 'column' ? ctx.columns.find((c) => c.id === (rule.trigger as { columnId: string }).columnId)?.title : undefined;
  const names = {
    users: Object.fromEntries((targets?.users ?? []).map((u) => [u.id, u.name])),
    groups: Object.fromEntries([...(targets?.named ?? []), ...(targets?.groups ?? [])].map((g) => [g.id, g.name])),
  };
  const rep = s.repeat
    ? ` · wiederholt ${s.repeat.kind === 'interval' ? `alle ${s.repeat.minutes / 60} Std.` : 'an Werktagen'}, höchstens ${s.repeat.maxCount}×`
    : '';
  return `${offsetText(rule.trigger, s.offset, col)} · ${recipientsText(s.recipients, names, rule.scope === 'personal')}${rep}`;
}

/* ---------- Abschnitt in den Kartendetails (U01) ---------- */

export function CardReminders({ ctx, cardTitle }: { ctx: Ctx & { cardId: string }; cardTitle: string }) {
  const { data: rules } = useCardReminders(ctx.cardId);
  const { data: targets } = useReminderTargets(ctx.boardId, true);
  const [edit, setEdit] = useState<{ rule: Draft; id?: string } | null>(null);
  const [snoozing, setSnoozing] = useState(false);
  const list = rules ?? [];
  const overrideOf = (id: string) => list.find((r) => r.overridesRuleId === id);
  return (
    <Section
      id="kd-erinnerungen"
      title="Erinnerungen & Übergaben"
      icon={<BellRing size={16} />}
      aside={
        <>
          <SectionAction onClick={() => setSnoozing(true)} label="Mich erinnern">
            <BellPlus size={15} aria-hidden /> <span className="max-sm:sr-only">Mich erinnern</span>
          </SectionAction>
          <SectionAction onClick={() => setEdit({ rule: newDraft(ctx.canEditCard ? 'card' : 'personal') })} label="Regel hinzufügen">
            <Plus size={15} aria-hidden /> <span className="max-sm:sr-only">Regel</span>
          </SectionAction>
        </>
      }
    >
      {!rules && <p className="text-sm text-[var(--ink-soft)]">Lade …</p>}
      {rules && list.length === 0 && (
        <p className="text-sm text-[var(--ink-soft)]">Keine Erinnerungen. Mit „Mich erinnern“ legst du eine persönliche Wiedervorlage an.</p>
      )}
      <ul className="flex flex-col divide-y divide-[var(--line)]" aria-label="Erinnerungsregeln">
        {list.map((r) => (
          <RuleRow
            key={r.id}
            rule={r}
            ctx={ctx}
            targets={targets}
            override={r.scope === 'board' ? overrideOf(r.id) : undefined}
            onEdit={() => setEdit({ rule: draftOf(r), id: r.id })}
            onCustomize={() => setEdit({ rule: { ...draftOf(r), scope: 'card', overridesRuleId: r.id, name: r.name } })}
          />
        ))}
      </ul>
      {edit && <ReminderEditor ctx={ctx} initial={edit.rule} ruleId={edit.id} onClose={() => setEdit(null)} targets={targets} />}
      <SnoozeDialog open={snoozing} onClose={() => setSnoozing(false)} cardId={ctx.cardId} cardTitle={cardTitle} />
    </Section>
  );
}

/** Boardstandards verwalten (Boardmenü → Erinnerungen) */
export function BoardReminders({ ctx, open, onClose }: { ctx: Ctx; open: boolean; onClose: () => void }) {
  const { data: rules } = useBoardReminders(ctx.boardId, open);
  const { data: targets } = useReminderTargets(ctx.boardId, open);
  const [edit, setEdit] = useState<{ rule: Draft; id?: string } | null>(null);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Erinnerungen: Boardstandards" side="center">
      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-[var(--ink-soft)]">
          Boardstandards gelten für alle Karten dieses Boards. Auf einer Karte lassen sie sich anpassen oder pausieren; persönliche Erinnerungen bleiben davon
          unabhängig.
        </p>
        {ctx.isOwner && (
          <Button className="self-start" onClick={() => setEdit({ rule: newDraft('board') })}>
            <Plus size={16} aria-hidden /> Boardstandard hinzufügen
          </Button>
        )}
        <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]" aria-label="Boardstandards">
          {(rules ?? []).map((r) => (
            <RuleRow key={r.id} rule={r} ctx={ctx} targets={targets} onEdit={() => setEdit({ rule: draftOf(r), id: r.id })} />
          ))}
          {rules?.length === 0 && <li className="p-3 text-sm text-[var(--ink-soft)]">Keine Boardstandards.</li>}
        </ul>
      </div>
      {edit && <ReminderEditor ctx={ctx} initial={edit.rule} ruleId={edit.id} onClose={() => setEdit(null)} targets={targets} />}
    </Sheet>
  );
}

/** Regel nennt eine inzwischen gelöschte Gruppe (N01) – andere Empfänger bleiben aktiv */
function hasDeletedGroup(rule: ReminderRule, targets: ReminderTargets | undefined): boolean {
  if (!targets) return false;
  const known = new Set([...(targets.named ?? []), ...targets.groups].map((g) => g.id));
  return rule.stages.some((s) => s.recipients.groups.some((g) => !known.has(g)));
}

function RuleRow({
  rule,
  ctx,
  targets,
  override,
  onEdit,
  onCustomize,
}: {
  rule: ReminderRule;
  ctx: Ctx;
  targets: ReminderTargets | undefined;
  override?: ReminderRule;
  onEdit: () => void;
  onCustomize?: () => void;
}) {
  const [history, setHistory] = useState(false);
  const own = rule.scope === 'personal' ? rule.ownerId === ctx.meId : rule.scope === 'card' ? ctx.canEditCard : ctx.isOwner;
  const replaced = !!override;
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) toast.info(ok);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      refreshReminders();
    }
  };
  const pauseHere = () =>
    run(
      () =>
        api.post(`/api/cards/${ctx.cardId}/reminders`, {
          ...draftOf(rule),
          scope: 'card',
          overridesRuleId: rule.id,
          enabled: false,
        }),
      'Boardstandard für diese Karte pausiert.',
    );
  return (
    <li className={cx('flex flex-col gap-1 py-2.5', replaced && 'opacity-60')}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
            <span className="rounded bg-black/5 px-1.5 py-px text-[11px] font-medium text-[var(--ink-soft)] dark:bg-white/10">
              {rule.overridesRuleId ? 'Angepasster Boardstandard' : SCOPE_LABEL[rule.scope]}
            </span>
            <span className="break-words">
              {rule.name ?? (rule.trigger.type === 'ready' ? 'Wenn Aufgabe startbereit' : offsetText(rule.trigger, rule.stages[0]!.offset))}
            </span>
            {!rule.enabled && <span className="text-xs font-normal text-amber-700 dark:text-amber-300">pausiert</span>}
            {hasDeletedGroup(rule, targets) && (
              <span className="text-xs font-normal text-amber-700 dark:text-amber-300" title="Andere gültige Empfänger bleiben aktiv.">
                Empfängergruppe gelöscht
              </span>
            )}
          </p>
          <ul className="mt-0.5 text-xs text-[var(--ink-soft)]">
            {rule.stages.map((s, i) => (
              <li key={i} className="break-words">
                {rule.stages.length > 1 ? `${i + 1}. ` : ''}
                {stageLine(rule, s, ctx, targets)}
              </li>
            ))}
            <li>
              App{rule.channels.email ? ' + E-Mail' : ''}
              {rule.channels.push ? ' + Push' : ''} (nach deinen Einstellungen){rule.onlyOpen ? ' · nur wenn noch offen' : ''}
            </li>
          </ul>
          {ctx.cardId && (
            <p className="mt-0.5 text-xs font-medium">
              {replaced ? 'Für diese Karte angepasst – siehe unten' : rule.next ? `Nächste: ${formatWhen(rule.next)}` : (rule.state ?? '')}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownTrigger asChild>
            <IconButton label={`Aktionen für Regel ${rule.name ?? ''}`.trim()}>
              <MoreHorizontal size={18} />
            </IconButton>
          </DropdownTrigger>
          <DropdownContent>
            {own && (
              <DropdownItem icon={<Pencil size={16} />} onSelect={onEdit}>
                Bearbeiten
              </DropdownItem>
            )}
            {own && (
              <DropdownItem
                icon={rule.enabled ? <Pause size={16} /> : <Play size={16} />}
                onSelect={() =>
                  void run(() => api.patch(`/api/reminders/${rule.id}`, { enabled: !rule.enabled }), rule.enabled ? 'Regel pausiert.' : 'Regel läuft wieder.')
                }
              >
                {rule.enabled ? 'Pausieren' : 'Fortsetzen'}
              </DropdownItem>
            )}
            {rule.scope === 'board' && ctx.cardId && ctx.canEditCard && !replaced && onCustomize && (
              <>
                <DropdownItem icon={<SlidersHorizontal size={16} />} onSelect={onCustomize}>
                  Für diese Karte anpassen
                </DropdownItem>
                <DropdownItem icon={<Pause size={16} />} onSelect={() => void pauseHere()}>
                  Für diese Karte pausieren
                </DropdownItem>
              </>
            )}
            {rule.overridesRuleId && own && (
              <DropdownItem icon={<RotateCcw size={16} />} onSelect={() => void run(() => api.del(`/api/reminders/${rule.id}`), 'Boardstandard gilt wieder.')}>
                Auf Boardstandard zurücksetzen
              </DropdownItem>
            )}
            {ctx.cardId && (
              <DropdownItem icon={<History size={16} />} onSelect={() => setHistory((h) => !h)}>
                {history ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
              </DropdownItem>
            )}
            {own && !rule.overridesRuleId && (
              <DropdownItem
                danger
                icon={<Trash2 size={16} />}
                onSelect={() => {
                  if (!window.confirm(rule.scope === 'board' ? 'Boardstandard für alle Karten entfernen?' : 'Regel entfernen?')) return;
                  void run(() => api.del(`/api/reminders/${rule.id}`));
                }}
              >
                Entfernen
              </DropdownItem>
            )}
          </DropdownContent>
        </DropdownMenu>
      </div>
      {history && ctx.cardId && <RunHistory ruleId={rule.id} cardId={ctx.cardId} />}
    </li>
  );
}

function RunHistory({ ruleId, cardId }: { ruleId: string; cardId: string }) {
  const { data } = useReminderRuns(ruleId, cardId, true);
  if (!data) return <p className="text-xs text-[var(--ink-soft)]">Lade …</p>;
  if (!data.length) return <p className="text-xs text-[var(--ink-soft)]">Noch keine Ausführungen geplant.</p>;
  return (
    <ul aria-label="Verlauf der Regel" className="ml-1 border-l-2 border-[var(--line)] pl-3 text-xs">
      {data.map((r) => (
        <li key={r.id} className="py-0.5">
          <span className="font-medium">{formatWhen(r.dueAt)}</span> · Stufe {r.stage + 1}
          {r.occurrence ? ` (Wiederholung ${r.occurrence})` : ''} ·{' '}
          <span className={r.status === 'failed' ? 'text-rose-600' : ''}>{REMINDER_STATUS_LABEL[r.status]}</span>
          {r.result ? ` – ${r.result}` : ''}
        </li>
      ))}
    </ul>
  );
}

/* ---------- Mich erinnern ---------- */

function SnoozeDialog({ open, onClose, cardId, cardTitle }: { open: boolean; onClose: () => void; cardId: string; cardTitle: string }) {
  const [custom, setCustom] = useState('');
  const go = async (at: Date) => {
    try {
      await snooze(cardId, at);
      toast.info(`Du wirst ${formatWhen(at.toISOString())} an „${cardTitle}“ erinnert.`);
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Mich erinnern" side="center">
      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-[var(--ink-soft)]">Nur für dich – Frist und Erinnerungen anderer bleiben unverändert.</p>
        {snoozeChoices().map((c) => (
          <Button key={c.label} onClick={() => void go(c.at)} className="justify-start">
            {c.label}
          </Button>
        ))}
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (custom) void go(new Date(custom));
          }}
        >
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-medium">
            Anderer Zeitpunkt
            <input type="datetime-local" className={field} value={custom} onChange={(e) => setCustom(e.target.value)} />
          </label>
          <Button type="submit" variant="primary" disabled={!custom}>
            Erinnern
          </Button>
        </form>
      </div>
    </Sheet>
  );
}

/* ---------- Regel-Editor in vier Schritten (U02) ---------- */

type Draft = Omit<ReminderRule, 'id' | 'boardId' | 'cardId' | 'ownerId' | 'version' | 'createdBy' | 'createdAt' | 'updatedAt' | 'timeZone'> & {
  overridesRuleId: string | null;
};

function newDraft(scope: ReminderScope): Draft {
  return {
    scope,
    overridesRuleId: null,
    name: null,
    enabled: true,
    trigger: { type: 'due' },
    stages: [
      {
        offset: { mode: 'duration', minutes: -1440 },
        recipients: scope === 'personal' ? { me: true, users: [], groups: [] } : { assignees: true, users: [], groups: [] },
      },
    ],
    channels: { email: true, push: true },
    onlyOpen: true,
    includeActor: false,
  };
}

function draftOf(r: ReminderRule): Draft {
  return {
    scope: r.scope,
    overridesRuleId: r.overridesRuleId,
    name: r.name,
    enabled: r.enabled,
    trigger: r.trigger,
    stages: r.stages,
    channels: r.channels,
    onlyOpen: r.onlyOpen,
    includeActor: r.includeActor,
  };
}

const TRIGGERS: [ReminderTrigger['type'], string][] = [
  ['due', 'Fälligkeit'],
  ['start', 'Start'],
  ['fixed', 'Fester Termin'],
  ['column', 'Wechsel in eine Spalte'],
  ['event', 'Aufgabenereignis'],
  ['ready', 'Aufgabe wird startbereit (Übergabe)'],
];

function ReminderEditor({
  ctx,
  initial,
  ruleId,
  onClose,
  targets,
}: {
  ctx: Ctx;
  initial: Draft;
  ruleId?: string;
  onClose: () => void;
  targets: ReminderTargets | undefined;
}) {
  const [d, setD] = useState<Draft>(initial);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ReminderPreview | null>(null);
  const personal = d.scope === 'personal';
  const timeBased = d.trigger.type === 'due' || d.trigger.type === 'start';
  const valid =
    d.stages.length > 0 &&
    (d.trigger.type !== 'column' || !!(d.trigger as { columnId: string }).columnId) &&
    (d.trigger.type !== 'fixed' || !!(d.trigger as { date: string }).date);
  const body = useMemo(() => ({ ...d, name: d.name || null }), [d]);

  // Vorschau ohne Versand (Testmodus): nächste Zeitpunkte und aktuell passende Empfänger
  useEffect(() => {
    if (!ctx.cardId || !valid) return setPreview(null);
    const t = setTimeout(() => {
      api
        .post<ReminderPreview>(`/api/cards/${ctx.cardId}/reminders/preview`, body)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [body, ctx.cardId, valid]);

  const setStage = (i: number, s: ReminderStage) => setD((x) => ({ ...x, stages: x.stages.map((y, j) => (j === i ? s : y)) }));
  const setTrigger = (type: ReminderTrigger['type']) => {
    const t1 = new Date(Date.now() + 86_400_000);
    const tomorrow = `${t1.getFullYear()}-${String(t1.getMonth() + 1).padStart(2, '0')}-${String(t1.getDate()).padStart(2, '0')}`;
    const trig: ReminderTrigger =
      type === 'fixed'
        ? { type, date: tomorrow, time: '09:00' }
        : type === 'column'
          ? { type, columnId: ctx.columns[0]?.id ?? '' }
          : type === 'event'
            ? { type, event: 'completed' }
            : { type };
    const at: ReminderOffset =
      type === 'due' ? { mode: 'duration', minutes: -1440 } : type === 'start' ? { mode: 'calendar', days: 0, time: '09:00' } : { mode: 'at' };
    setD((x) => ({ ...x, trigger: trig, stages: x.stages.map((s) => ({ ...s, offset: at })) }));
  };

  const scopeOptions: [ReminderScope, string][] = ctx.cardId
    ? [...(ctx.canEditCard ? ([['card', 'Für diese Karte (alle Empfänger)']] as [ReminderScope, string][]) : []), ['personal', 'Nur für mich']]
    : [['board', 'Boardstandard']];

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={ruleId ? 'Regel bearbeiten' : d.overridesRuleId ? 'Boardstandard anpassen' : 'Neue Regel'}
      side="center"
    >
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            if (ruleId) {
              const { scope: _s, overridesRuleId: _o, ...patch } = body;
              await api.patch(`/api/reminders/${ruleId}`, patch);
            } else if (ctx.cardId) await api.post(`/api/cards/${ctx.cardId}/reminders`, body);
            else await api.post(`/api/boards/${ctx.boardId}/reminders`, body);
            toast.info('Regel gespeichert.');
            refreshReminders();
            onClose();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {!ruleId && !d.overridesRuleId && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Vorlagen">
            {REMINDER_TEMPLATES.filter((t) => !personal || t.key === 'before-due' || t.key === 'overdue').map((t) => (
              <button
                key={t.key}
                type="button"
                className="min-h-10 rounded-full border border-[var(--line)] px-3 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                onClick={() =>
                  setD((x) => ({
                    ...x,
                    name: t.label,
                    trigger: t.trigger.type === 'column' ? { type: 'column', columnId: ctx.columns[0]?.id ?? '' } : t.trigger,
                    stages: t.stages.map((s) => ({ ...s, recipients: personal ? { me: true, users: [], groups: [] } : s.recipients })),
                  }))
                }
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
        {!ruleId && !d.overridesRuleId && scopeOptions.length > 1 && (
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Gilt für
            <select
              className={field}
              value={d.scope}
              onChange={(e) => {
                const scope = e.target.value as ReminderScope;
                setD((x) => ({
                  ...x,
                  scope,
                  stages: x.stages.map((s) => ({
                    ...s,
                    recipients: scope === 'personal' ? { me: true, users: [], groups: [] } : { assignees: true, users: [], groups: [] },
                  })),
                }));
              }}
            >
              {scopeOptions.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        )}

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">1 · Auslöser</legend>
          <select aria-label="Auslöser" className={field} value={d.trigger.type} onChange={(e) => setTrigger(e.target.value as ReminderTrigger['type'])}>
            {TRIGGERS.filter(([t]) => !personal || ['due', 'start', 'fixed'].includes(t)).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          {d.trigger.type === 'fixed' && (
            <div className="flex flex-wrap gap-2">
              <label className="flex flex-col gap-1 text-sm">
                Datum
                <input
                  type="date"
                  className={field}
                  value={d.trigger.date}
                  onChange={(e) => setD((x) => ({ ...x, trigger: { ...(x.trigger as { type: 'fixed'; date: string; time: string }), date: e.target.value } }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Uhrzeit
                <input
                  type="time"
                  className={field}
                  value={d.trigger.time}
                  onChange={(e) => setD((x) => ({ ...x, trigger: { ...(x.trigger as { type: 'fixed'; date: string; time: string }), time: e.target.value } }))}
                />
              </label>
            </div>
          )}
          {d.trigger.type === 'column' && (
            <label className="flex flex-col gap-1 text-sm">
              Spalte
              <select
                className={field}
                value={d.trigger.columnId}
                onChange={(e) => setD((x) => ({ ...x, trigger: { type: 'column', columnId: e.target.value } }))}
              >
                {ctx.columns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          {d.trigger.type === 'event' && (
            <label className="flex flex-col gap-1 text-sm">
              Ereignis
              <select
                className={field}
                value={d.trigger.event}
                onChange={(e) => setD((x) => ({ ...x, trigger: { type: 'event', event: e.target.value as ReminderEvent } }))}
              >
                {REMINDER_EVENTS.map((ev) => (
                  <option key={ev} value={ev}>
                    {EVENT_LABEL[ev]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {d.trigger.type === 'ready' && (
            <p className="text-xs text-[var(--ink-soft)]">
              Sobald alle Vorgänger erledigt sind, die Aufgabe offen und nicht blockiert ist und ihr Start erreicht ist. Gilt nur für Karten mit Vorgängern.
            </p>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-semibold">2 · Zeitpunkte und 3 · Empfänger</legend>
          {d.stages.map((s, i) => (
            <StageEditor
              key={i}
              index={i}
              total={d.stages.length}
              stage={s}
              trigger={d.trigger}
              personal={personal}
              targets={targets}
              advanced={advanced}
              preview={preview?.stages[i]}
              onChange={(ns) => setStage(i, ns)}
              onRemove={() => setD((x) => ({ ...x, stages: x.stages.filter((_, j) => j !== i) }))}
            />
          ))}
          {d.trigger.type !== 'fixed' && d.stages.length < 8 && (
            <Button
              type="button"
              className="self-start"
              onClick={() => setD((x) => ({ ...x, stages: [...x.stages, { ...x.stages[x.stages.length - 1]!, text: null, repeat: null }] }))}
            >
              <Plus size={16} aria-hidden /> Weitere Stufe
            </Button>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold">4 · Kanäle</legend>
          <p className="text-xs text-[var(--ink-soft)]">
            In der App immer. Zusätzlich – soweit die Empfänger das in ihren Einstellungen erlauben; Ruhezeiten gelten:
          </p>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="size-5 accent-[var(--color-accent)]"
              checked={d.channels.email}
              onChange={(e) => setD((x) => ({ ...x, channels: { ...x.channels, email: e.target.checked } }))}
            />
            E-Mail
          </label>
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="size-5 accent-[var(--color-accent)]"
              checked={d.channels.push}
              onChange={(e) => setD((x) => ({ ...x, channels: { ...x.channels, push: e.target.checked } }))}
            />
            Push
          </label>
        </fieldset>

        <button type="button" aria-expanded={advanced} onClick={() => setAdvanced((a) => !a)} className="min-h-11 self-start text-sm font-medium text-accent-fg">
          {advanced ? 'Weniger Optionen' : 'Erweitert: Bedingungen, Wiederholung, Name'}
        </button>
        {advanced && (
          <div className="flex flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
            <label className="flex flex-col gap-1 text-sm font-medium">
              Name (optional)
              <input
                className={field}
                maxLength={80}
                value={d.name ?? ''}
                onChange={(e) => setD((x) => ({ ...x, name: e.target.value }))}
                placeholder="z. B. Frist absichern"
              />
            </label>
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-[var(--color-accent)]"
                checked={d.onlyOpen}
                onChange={(e) => setD((x) => ({ ...x, onlyOpen: e.target.checked }))}
              />
              Nur solange die Aufgabe offen ist
            </label>
            {(d.trigger.type === 'column' || d.trigger.type === 'event') && (
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  className="size-5 accent-[var(--color-accent)]"
                  checked={d.includeActor}
                  onChange={(e) => setD((x) => ({ ...x, includeActor: e.target.checked }))}
                />
                Auslösende Person mitbenachrichtigen
              </label>
            )}
          </div>
        )}

        {preview?.state && (
          <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            {preview.state}
          </p>
        )}
        <p className="text-xs text-[var(--ink-soft)]">
          Die Vorschau sendet nichts. Geplante Zeitpunkte gelten für die Meldung in der App; E-Mail und Push können etwas später ankommen.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !valid}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

type OffsetKind = 'at' | 'before' | 'after' | 'day-before' | 'day-after' | 'same-day' | 'wd-before' | 'wd-after';

function offsetKind(o: ReminderOffset): OffsetKind {
  if (o.mode === 'at') return 'at';
  if (o.mode === 'duration') return o.minutes < 0 ? 'before' : 'after';
  if (o.mode === 'workdays') return o.days < 0 ? 'wd-before' : 'wd-after';
  return o.days === 0 ? 'same-day' : o.days < 0 ? 'day-before' : 'day-after';
}

function StageEditor({
  index,
  total,
  stage,
  trigger,
  personal,
  targets,
  advanced,
  preview,
  onChange,
  onRemove,
}: {
  index: number;
  total: number;
  stage: ReminderStage;
  trigger: ReminderTrigger;
  personal: boolean;
  targets: ReminderTargets | undefined;
  advanced: boolean;
  preview: ReminderPreview['stages'][number] | undefined;
  onChange: (s: ReminderStage) => void;
  onRemove: () => void;
}) {
  const kind = offsetKind(stage.offset);
  const isEvent = trigger.type === 'column' || trigger.type === 'event' || trigger.type === 'ready';
  const timed = trigger.type === 'due' || trigger.type === 'start';
  const amount = stage.offset.mode === 'duration' ? Math.abs(stage.offset.minutes) : stage.offset.mode === 'calendar' || stage.offset.mode === 'workdays' ? Math.abs(stage.offset.days) : 0;
  const unit =
    stage.offset.mode === 'duration' && stage.offset.minutes % 1440 === 0 && stage.offset.minutes !== 0 && Math.abs(stage.offset.minutes) >= 2880
      ? 1440
      : stage.offset.mode === 'duration' && stage.offset.minutes % 60 === 0
        ? 60
        : 1;
  const time = stage.offset.mode === 'calendar' || stage.offset.mode === 'workdays' ? stage.offset.time : '09:00';
  const holidays = stage.offset.mode === 'workdays' ? (stage.offset.holidays ?? null) : accountPref<HolidayRegion | null>('holidayRegion', null);
  const workdays = kind === 'wd-before' || kind === 'wd-after';
  const setKind = (k: OffsetKind) => {
    const o: ReminderOffset =
      k === 'at'
        ? { mode: 'at' }
        : k === 'before'
          ? { mode: 'duration', minutes: -(amount || 24) * (unit === 1 && !amount ? 60 : unit) }
          : k === 'after'
            ? { mode: 'duration', minutes: (amount || 4) * (unit === 1 && !amount ? 60 : unit) }
            : k === 'wd-before' || k === 'wd-after'
              ? { mode: 'workdays', days: (k === 'wd-before' ? -1 : 1) * (amount || 1), time, holidays }
              : { mode: 'calendar', days: k === 'same-day' ? 0 : k === 'day-before' ? -(amount || 1) : amount || 1, time };
    onChange({ ...stage, offset: o });
  };
  const r = stage.recipients;
  const setR = (p: Partial<typeof r>) => onChange({ ...stage, recipients: { ...r, ...p } });
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm font-medium">{total > 1 ? `Stufe ${index + 1}` : 'Zeitpunkt'}</span>
        {total > 1 && (
          <IconButton label={`Stufe ${index + 1} entfernen`} type="button" onClick={onRemove}>
            <X size={16} />
          </IconButton>
        )}
      </div>
      {trigger.type !== 'fixed' && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-0 flex-1 basis-48 flex-col gap-1 text-sm">
            Wann
            <select className={field} value={kind} onChange={(e) => setKind(e.target.value as OffsetKind)}>
              <option value="at">{isEvent ? 'Sofort' : trigger.type === 'due' ? 'Bei Fälligkeit' : 'Zum Start'}</option>
              {timed && <option value="before">Dauer davor</option>}
              <option value="after">{isEvent ? 'Nach einer Dauer' : 'Dauer danach'}</option>
              {timed && <option value="same-day">Am selben Tag um …</option>}
              {timed && <option value="day-before">Kalendertage davor um …</option>}
              {timed && <option value="day-after">Kalendertage danach um …</option>}
              {timed && <option value="wd-before">Arbeitstage davor um …</option>}
              {timed && <option value="wd-after">Arbeitstage danach um …</option>}
            </select>
          </label>
          {(kind === 'before' || kind === 'after') && (
            <>
              <label className="flex w-24 flex-col gap-1 text-sm">
                Anzahl
                <input
                  type="number"
                  min={1}
                  max={unit === 1 ? 10000 : 1000}
                  className={field}
                  value={amount / unit || 1}
                  onChange={(e) =>
                    onChange({
                      ...stage,
                      offset: { mode: 'duration', minutes: (kind === 'before' ? -1 : 1) * Math.max(1, Number(e.target.value) || 1) * unit },
                    })
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Einheit
                <select
                  className={field}
                  value={unit}
                  onChange={(e) =>
                    onChange({
                      ...stage,
                      offset: { mode: 'duration', minutes: (kind === 'before' ? -1 : 1) * Math.max(1, amount / unit || 1) * Number(e.target.value) },
                    })
                  }
                >
                  <option value={1}>Minuten</option>
                  <option value={60}>Stunden</option>
                  <option value={1440}>Tage (je 24 Std.)</option>
                </select>
              </label>
            </>
          )}
          {(kind === 'day-before' || kind === 'day-after' || kind === 'same-day' || workdays) && (
            <>
              {kind !== 'same-day' && (
                <label className="flex w-24 flex-col gap-1 text-sm">
                  Tage
                  <input
                    type="number"
                    min={1}
                    max={60}
                    className={field}
                    value={amount || 1}
                    onChange={(e) =>
                      onChange({
                        ...stage,
                        offset: workdays
                          ? { mode: 'workdays', days: (kind === 'wd-before' ? -1 : 1) * Math.max(1, Number(e.target.value) || 1), time, holidays }
                          : { mode: 'calendar', days: (kind === 'day-before' ? -1 : 1) * Math.max(1, Number(e.target.value) || 1), time },
                      })
                    }
                  />
                </label>
              )}
              <label className="flex flex-col gap-1 text-sm">
                Uhrzeit
                <input
                  type="time"
                  className={field}
                  value={time}
                  onChange={(e) =>
                    onChange({
                      ...stage,
                      offset:
                        stage.offset.mode === 'workdays'
                          ? { ...stage.offset, time: e.target.value || '09:00' }
                          : { mode: 'calendar', days: stage.offset.mode === 'calendar' ? stage.offset.days : 0, time: e.target.value || '09:00' },
                    })
                  }
                />
              </label>
              {stage.offset.mode === 'workdays' && (
                <div className="basis-full">
                  <HolidaySelect value={stage.offset.holidays} onChange={(h) => stage.offset.mode === 'workdays' && onChange({ ...stage, offset: { ...stage.offset, holidays: h } })} />
                </div>
              )}
            </>
          )}
        </div>
      )}
      {personal ? (
        <p className="text-sm text-[var(--ink-soft)]">Empfänger: nur du.</p>
      ) : (
        <fieldset className="flex flex-col gap-0.5">
          <legend className="mb-1 text-sm">Empfänger</legend>
          {(
            [
              ['assignees', 'Alle Zuständigen (zum Zeitpunkt der Meldung)'],
              ['owner', 'Hauptverantwortliche'],
              ['watchers', 'Beobachtende'],
            ] as const
          ).map(([k, l]) => (
            <label key={k} className="flex min-h-10 items-center gap-3 text-sm">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={!!r[k]} onChange={(e) => setR({ [k]: e.target.checked })} />
              {l}
            </label>
          ))}
          {!!targets?.groups.length && (
            <details className="mt-1" open={r.groups.length > 0}>
              <summary className="min-h-10 cursor-pointer py-2 text-sm font-medium">Gruppen{r.groups.length ? ` (${r.groups.length})` : ''}</summary>
              {targets.groups.map((g) => (
                <label key={g.id} className="flex min-h-10 items-center gap-3 pl-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-5 accent-[var(--color-accent)]"
                    checked={r.groups.includes(g.id)}
                    onChange={() => setR({ groups: toggle(r.groups, g.id) })}
                  />
                  {g.name}
                </label>
              ))}
              <p className="pl-2 text-xs text-[var(--ink-soft)]">An die jeweils aktuellen Mitglieder mit Zugriff auf dieses Board.</p>
            </details>
          )}
          <details className="mt-1" open={r.users.length > 0}>
            <summary className="min-h-10 cursor-pointer py-2 text-sm font-medium">Bestimmte Personen{r.users.length ? ` (${r.users.length})` : ''}</summary>
            <div className="max-h-48 overflow-y-auto">
              {(targets?.users ?? []).map((u) => (
                <label key={u.id} className="flex min-h-10 items-center gap-3 pl-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-5 accent-[var(--color-accent)]"
                    checked={r.users.includes(u.id)}
                    onChange={() => setR({ users: toggle(r.users, u.id) })}
                  />
                  {u.name}
                </label>
              ))}
            </div>
          </details>
        </fieldset>
      )}
      {advanced && (
        <>
          <label className="flex flex-col gap-1 text-sm">
            Eigener Text (optional)
            <input
              className={field}
              maxLength={300}
              value={stage.text ?? ''}
              onChange={(e) => onChange({ ...stage, text: e.target.value || null })}
              placeholder="z. B. Bitte heute noch prüfen"
            />
          </label>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-sm">
              Wiederholen
              <select
                className={field}
                value={stage.repeat?.kind ?? 'none'}
                onChange={(e) =>
                  onChange({
                    ...stage,
                    repeat:
                      e.target.value === 'none'
                        ? null
                        : e.target.value === 'interval'
                          ? { kind: 'interval', minutes: 240, maxCount: 3 }
                          : { kind: 'weekdays', days: [1, 2, 3, 4, 5], time: '09:00', maxCount: 5 },
                  })
                }
              >
                <option value="none">Nicht wiederholen</option>
                <option value="interval">In festen Abständen</option>
                <option value="weekdays">Montag bis Freitag um …</option>
              </select>
            </label>
            {stage.repeat?.kind === 'interval' && (
              <label className="flex w-28 flex-col gap-1 text-sm">
                alle … Std.
                <input
                  type="number"
                  min={1}
                  max={336}
                  className={field}
                  value={stage.repeat.minutes / 60}
                  onChange={(e) => onChange({ ...stage, repeat: { ...stage.repeat!, minutes: Math.max(1, Number(e.target.value) || 1) * 60 } as never })}
                />
              </label>
            )}
            {stage.repeat?.kind === 'weekdays' && (
              <label className="flex flex-col gap-1 text-sm">
                Uhrzeit
                <input
                  type="time"
                  className={field}
                  value={stage.repeat.time}
                  onChange={(e) => onChange({ ...stage, repeat: { ...stage.repeat!, time: e.target.value || '09:00' } as never })}
                />
              </label>
            )}
            {stage.repeat && (
              <label className="flex w-28 flex-col gap-1 text-sm">
                höchstens
                <input
                  type="number"
                  min={1}
                  max={50}
                  className={field}
                  value={stage.repeat.maxCount}
                  onChange={(e) => onChange({ ...stage, repeat: { ...stage.repeat!, maxCount: Math.min(50, Math.max(1, Number(e.target.value) || 1)) } })}
                />
              </label>
            )}
          </div>
          {stage.repeat && (
            <p className="text-xs text-[var(--ink-soft)]">Endet spätestens nach dieser Anzahl – oder sobald die Bedingung nicht mehr zutrifft.</p>
          )}
        </>
      )}
      {preview && (
        <div className="rounded-lg bg-black/[0.03] p-2 text-xs dark:bg-white/5" aria-live="polite">
          {preview.at ? (
            <p>
              <span className="font-medium">{formatWhen(preview.at)}</span>
              {preview.past ? ' – Zeitpunkt bereits vergangen, wird nicht nachträglich gesendet' : ''}
            </p>
          ) : (
            isEvent && <p>Beim nächsten passenden Ereignis</p>
          )}
          <p>{preview.recipients.length ? `Derzeit an: ${preview.recipients.map((x) => x.name).join(', ')}` : ''}</p>
          {preview.problems.map((p) => (
            <p key={p} className="text-amber-700 dark:text-amber-300">
              {p}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
